// + Attempt dialog. Five fields: Title (dropdown w/ Manage), Date,
// Contact made (Yes/No), Outcome (dropdown w/ Manage), Competitor (Yes/No).
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { X, CalendarIcon, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toLocalYMD, fromYMD, todayYMD } from "@/lib/pp/dates";
import {
  createAttempt,
  updateAttempt,
  listTitles,
  createTitle,
  renameTitle,
  deleteTitle,
  listOutcomes,
  createOutcome,
  renameOutcome,
  deleteOutcome,
} from "@/lib/pp/bdApi";
import ManageListDialog from "@/components/pp/ManageListDialog";

const YesNoToggle = ({ value, onChange, testId, redOnYes = false }) => (
  <div className="grid grid-cols-2 gap-2">
    <button
      type="button"
      onClick={() => onChange(false)}
      data-testid={`${testId}-no`}
      className={`px-4 py-2.5 rounded-lg border text-sm font-semibold transition ${
        value === false
          ? redOnYes
            ? "bg-[#2E7D5B] text-white border-[#2E7D5B]"
            : "bg-[#2E7D5B] text-white border-[#2E7D5B]"
          : "bg-white text-[#6B7A73] border-[#E5E2DC]"
      }`}
    >
      No
    </button>
    <button
      type="button"
      onClick={() => onChange(true)}
      data-testid={`${testId}-yes`}
      className={`px-4 py-2.5 rounded-lg border text-sm font-semibold transition ${
        value === true
          ? redOnYes
            ? "bg-[#B4351B] text-white border-[#B4351B]"
            : "bg-[#2E7D5B] text-white border-[#2E7D5B]"
          : "bg-white text-[#6B7A73] border-[#E5E2DC]"
      }`}
    >
      Yes
    </button>
  </div>
);

export default function AttemptFormDialog({
  open,
  onOpenChange,
  pharmacyId,
  attempt,          // existing attempt to edit, or null for new
  onSaved,
}) {
  const [titles, setTitles] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [manageTitles, setManageTitles] = useState(false);
  const [manageOutcomes, setManageOutcomes] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(todayYMD());
  const [contactMade, setContactMade] = useState(false);
  const [outcome, setOutcome] = useState("Pending");
  const [competitor, setCompetitor] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadLists = async () => {
    try {
      const [t, o] = await Promise.all([listTitles(), listOutcomes()]);
      setTitles(t);
      setOutcomes(o);
      // Ensure current selections still exist; if not, fall back to first
      if (!attempt) {
        if (t.length && !title) setTitle(t[0].name);
        if (o.length && !outcome) setOutcome(o[0].name);
      }
    } catch {
      toast.error("Couldn't load BD lists");
    }
  };

  useEffect(() => {
    if (!open) return;
    if (attempt) {
      setTitle(attempt.title || "");
      setDate(attempt.date || todayYMD());
      setContactMade(!!attempt.contact_made);
      setOutcome(attempt.outcome || "Pending");
      setCompetitor(!!attempt.competitor);
    } else {
      setTitle("");
      setDate(todayYMD());
      setContactMade(false);
      setOutcome("Pending");
      setCompetitor(false);
    }
    loadLists();
  }, [open, attempt]);

  const save = async () => {
    if (!title.trim()) {
      toast.error("Pick a title");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        pharmacy_id: pharmacyId,
        title: title.trim(),
        date,
        contact_made: contactMade,
        outcome: outcome.trim() || "Pending",
        competitor,
      };
      const saved = attempt
        ? await updateAttempt(attempt.id, payload)
        : await createAttempt(payload);
      toast.success(attempt ? "Attempt updated" : "Attempt saved");
      onSaved && onSaved(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-md p-0 gap-0"
          data-testid="bd-attempt-dialog"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E2DC]">
            <h3 className="text-base font-semibold text-[#1C2B26]">
              {attempt ? "Edit attempt" : "New attempt"}
            </h3>
          </div>

          <div className="p-5 space-y-5">
            {/* Title */}
            <div>
              <label className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#6B7A73] mb-1.5 block">
                Title
              </label>
              <Select value={title} onValueChange={setTitle}>
                <SelectTrigger
                  className="h-10 bg-white"
                  data-testid="bd-attempt-title"
                >
                  <SelectValue placeholder="Pick a title" />
                </SelectTrigger>
                <SelectContent>
                  {titles.map((t) => (
                    <SelectItem key={t.id} value={t.name}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => setManageTitles(true)}
                data-testid="bd-attempt-manage-titles"
                className="text-xs font-semibold text-[#2E7D5B] hover:text-[#1F5E43] inline-flex items-center gap-1 mt-1.5"
              >
                <Settings2 className="h-3 w-3" /> Manage titles…
              </button>
            </div>

            {/* Date */}
            <div>
              <label className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#6B7A73] mb-1.5 block">
                Date
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="bd-attempt-date"
                    className="w-full h-10 px-3 border border-[#E5E2DC] rounded-md bg-white flex items-center gap-2 text-sm text-[#1C2B26]"
                  >
                    <CalendarIcon className="h-4 w-4 text-[#6B7A73]" />
                    {date ? format(fromYMD(date), "PPP") : "Pick a date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date ? fromYMD(date) : undefined}
                    onSelect={(d) => d && setDate(toLocalYMD(d))}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Contact made */}
            <div>
              <label className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#6B7A73] mb-1.5 block">
                Contact made
              </label>
              <YesNoToggle
                value={contactMade}
                onChange={setContactMade}
                testId="bd-attempt-contact"
              />
            </div>

            {/* Outcome */}
            <div>
              <label className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#6B7A73] mb-1.5 block">
                Outcome
              </label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger
                  className="h-10 bg-white"
                  data-testid="bd-attempt-outcome"
                >
                  <SelectValue placeholder="Pick an outcome" />
                </SelectTrigger>
                <SelectContent>
                  {outcomes.map((o) => (
                    <SelectItem key={o.id} value={o.name}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => setManageOutcomes(true)}
                data-testid="bd-attempt-manage-outcomes"
                className="text-xs font-semibold text-[#2E7D5B] hover:text-[#1F5E43] inline-flex items-center gap-1 mt-1.5"
              >
                <Settings2 className="h-3 w-3" /> Manage outcomes…
              </button>
            </div>

            {/* Competitor */}
            <div>
              <label className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#6B7A73] mb-1.5 block">
                Competitor
              </label>
              <YesNoToggle
                value={competitor}
                onChange={setCompetitor}
                testId="bd-attempt-competitor"
                redOnYes
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 px-5 py-4 bg-[#FAF9F5] border-t border-[#E5E2DC]">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="bd-attempt-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#2E7D5B] hover:bg-[#25664A]"
              data-testid="bd-attempt-save"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ManageListDialog
        open={manageTitles}
        onOpenChange={setManageTitles}
        label="titles"
        listFn={listTitles}
        createFn={createTitle}
        renameFn={renameTitle}
        deleteFn={deleteTitle}
        onChanged={loadLists}
      />
      <ManageListDialog
        open={manageOutcomes}
        onOpenChange={setManageOutcomes}
        label="outcomes"
        listFn={listOutcomes}
        createFn={createOutcome}
        renameFn={renameOutcome}
        deleteFn={deleteOutcome}
        onChanged={loadLists}
      />
    </>
  );
}
